import { memo } from 'react';
import { Link } from 'react-router-dom';
import { UserAvatar } from '@/entities/user';
import type { Conversation } from '@/entities/message';
import { ROUTES } from '@/shared/config/constants';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';

export type ConversationItemProps = {
  conversation: Conversation;
  // Online indicator esta hard-coded como true por enquanto -- prop separada
  // pra quando a feature de presence via socket for ligada.
  online?: boolean;
};

const ConversationItemImpl = ({ conversation, online = true }: ConversationItemProps) => {
  const hasUnread = conversation.naoLidas > 0;
  const previewText = conversation.ultimaMensagem ?? 'Sem mensagens ainda';
  const ariaLabel = `Conversa com ${conversation.participanteNome}, última mensagem: ${previewText}${
    hasUnread ? `, ${conversation.naoLidas} mensagem${conversation.naoLidas > 1 ? 's' : ''} não lida${conversation.naoLidas > 1 ? 's' : ''}` : ''
  }`;

  return (
    <li role="listitem">
      <Link
        to={ROUTES.APP.CHAT_CONVERSATION(conversation.matchId)}
        aria-label={ariaLabel}
        className={cn(
          'flex items-center gap-3 rounded-2xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-all duration-150',
          'hover:bg-gray-50 hover:shadow-[0_2px_6px_rgba(16,24,40,0.08)]',
          'active:scale-[0.98]',
        )}
      >
        <div className="relative shrink-0">
          <UserAvatar
            nome={conversation.participanteNome}
            fotoUrl={conversation.participanteFotoUrl ?? null}
            size="md"
          />
          {online ? (
            <span
              aria-label="Online"
              className="absolute -bottom-0.5 -right-0.5 block h-3 w-3 rounded-full border-2 border-white bg-emerald-500"
            />
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p
              className={cn(
                'truncate text-base tracking-tight text-gray-900',
                hasUnread ? 'font-bold' : 'font-semibold',
              )}
            >
              {conversation.participanteNome}
            </p>
            {conversation.ultimaMensagemEm ? (
              <span
                className={cn(
                  'shrink-0 text-xs',
                  hasUnread ? 'font-semibold text-gray-900' : 'text-gray-400',
                )}
              >
                {formatRelative(conversation.ultimaMensagemEm)}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-3">
            <p
              className={cn(
                'truncate text-sm',
                hasUnread ? 'text-gray-700' : 'text-gray-500',
              )}
            >
              {previewText}
            </p>
            {hasUnread ? (
              <span
                aria-hidden
                className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-gray-900 px-1.5 text-xs font-semibold text-white"
              >
                {conversation.naoLidas > 99 ? '99+' : conversation.naoLidas}
              </span>
            ) : null}
          </div>
        </div>
      </Link>
    </li>
  );
};

export const ConversationItem = memo(ConversationItemImpl);
